/** Procedural plant geometries (merged, vertex-coloured) for instancing. */
import { BufferAttribute, BufferGeometry, Color, ConeGeometry, CylinderGeometry, PlaneGeometry, Vector3 } from 'three';
import { mergeGeometries as mergeRaw, mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** Merge geometries with mixed attributes/indexing: keeps position + normal only. */
export function mergeGeometries(parts: BufferGeometry[], _groups = false): BufferGeometry | null {
  const anyIndexed = parts.some((g) => g.index);
  const clean = parts.map((g) => {
    let c = g;
    if (anyIndexed && !g.index) {
      const idx = Array.from({ length: g.getAttribute('position').count }, (_, i) => i);
      c = g.clone();
      c.setIndex(idx);
    }
    for (const name of Object.keys(c.attributes)) if (name !== 'position' && name !== 'normal') c.deleteAttribute(name);
    return c;
  });
  return mergeRaw(clean, false);
}
import { tubeGeometry } from './geometry';

function paint(g: BufferGeometry, fn: (x: number, y: number, z: number, c: Color) => void): BufferGeometry {
  const p = g.getAttribute('position');
  const col = new Float32Array(p.count * 3);
  const c = new Color();
  for (let i = 0; i < p.count; i++) {
    fn(p.getX(i), p.getY(i), p.getZ(i), c);
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new BufferAttribute(col, 3));
  return g;
}

/** A single curved frond (plane bent along a parabola, tapered). Base at origin, +Y up, leaning +Z. */
function frond(length: number, width: number, droop: number, segs = 6): BufferGeometry {
  const g = new PlaneGeometry(width, length, 1, segs);
  const p = g.getAttribute('position');
  for (let i = 0; i < p.count; i++) {
    const t = (p.getY(i) + length / 2) / length; // 0 base .. 1 tip
    const taper = 1 - t * t * 0.85;
    p.setX(i, p.getX(i) * taper * (0.4 + 0.6 * Math.sin(t * Math.PI)));
    // bend: rise then droop
    const y = Math.sin(t * Math.PI * 0.6) * length * 0.8 - droop * t * t * length;
    const z = t * length * (0.6 + droop * 0.4);
    p.setY(i, y);
    p.setZ(i, z);
  }
  g.computeVertexNormals();
  return g;
}

/** Giant fern: N fronds radiating from a short base. Height ~ `size`. */
export function fernGeometry(size = 1.6, fronds = 7, base = '#1f4d2a', tip = '#8fd66a'): BufferGeometry {
  const parts: BufferGeometry[] = [];
  const b = new Color(base);
  const tp = new Color(tip);
  for (let i = 0; i < fronds; i++) {
    const f = frond(size * (0.85 + (i % 3) * 0.08), size * 0.28, 0.35 + (i % 2) * 0.15);
    f.rotateX(0.35);
    f.rotateY((i / fronds) * Math.PI * 2 + (i % 2) * 0.2);
    f.translate(0, size * 0.08, 0);
    parts.push(f);
  }
  const stem = new CylinderGeometry(size * 0.05, size * 0.09, size * 0.25, 6);
  stem.translate(0, size * 0.1, 0);
  parts.push(stem);
  const g = mergeGeometries(parts, false)!;
  return paint(g, (x, y, z, c) => {
    const t = Math.min(1, Math.max(0, y / (size * 0.9)));
    const d = Math.min(1, Math.hypot(x, z) / size);
    c.copy(b).lerp(tp, Math.max(t, d * 0.8));
  });
}

/** Horsetail (Calamites): tall jointed stalk with whorls of needle branches. */
export function horsetailGeometry(height = 4, base = '#3b6b2a', tip = '#a4d65e'): BufferGeometry {
  const parts: BufferGeometry[] = [];
  const joints = 6;
  const pts: Vector3[] = [];
  for (let i = 0; i <= joints; i++) pts.push(new Vector3(Math.sin(i * 1.7) * 0.06 * height * (i / joints), (i / joints) * height, Math.cos(i * 1.3) * 0.05 * height * (i / joints)));
  const stalk = tubeGeometry({
    points: pts,
    radius: (t) => height * 0.035 * (1 - t * 0.6) * (1 + 0.35 * Math.max(0, Math.sin(t * joints * Math.PI * 2) ** 8)),
    segments: joints * 3,
    radial: 7,
    caps: true,
  });
  parts.push(stalk);
  for (let j = 2; j <= joints; j++) {
    const y = (j / joints) * height;
    const n = 5;
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2 + j;
      const len = height * 0.22 * (1 - (j / joints) * 0.4);
      const br = new CylinderGeometry(0.006 * height, 0.014 * height, len, 4);
      br.translate(0, len / 2, 0);
      br.rotateZ(-0.9);
      br.rotateY(a);
      br.translate(pts[j].x, y, pts[j].z);
      parts.push(br);
    }
  }
  const g = mergeGeometries(parts, false)!;
  const b = new Color(base);
  const tp = new Color(tip);
  return paint(g, (_x, y, _z, c) => c.copy(b).lerp(tp, Math.min(1, y / height)));
}

/** Lepidodendron-style scale tree: tall trunk, forked crown with leafy tufts. */
export function scaleTreeGeometry(height = 12, trunk = '#4d3b2a', leaf = '#4e9a3a', leafTip = '#b6e36a'): BufferGeometry {
  const parts: BufferGeometry[] = [];
  const t = tubeGeometry({
    points: [new Vector3(0, 0, 0), new Vector3(0.1 * height * 0.1, height * 0.5, 0), new Vector3(0, height * 0.82, 0.05)],
    radius: [height * 0.05, height * 0.035, height * 0.025],
    segments: 8,
    radial: 8,
  });
  parts.push(t);
  const forks = 4;
  const crownTag: number[] = [];
  for (let i = 0; i < forks; i++) {
    const a = (i / forks) * Math.PI * 2 + 0.4;
    const len = height * 0.22;
    const br = new CylinderGeometry(height * 0.01, height * 0.022, len, 6);
    br.translate(0, len / 2, 0);
    br.rotateZ(-0.7 - (i % 2) * 0.25);
    br.rotateY(a);
    br.translate(0, height * 0.8, 0);
    parts.push(br);
    const tuftPos = new Vector3(Math.sin(a) * len * 0.75, height * 0.8 + len * 0.7, Math.cos(a) * len * 0.75);
    // tuft: cone bundle
    for (let k = 0; k < 5; k++) {
      const cone = new ConeGeometry(height * 0.07, height * 0.16, 5);
      cone.translate(0, -height * 0.02, 0);
      cone.rotateX(Math.PI * 0.75 + (k % 2) * 0.3);
      cone.rotateY((k / 5) * Math.PI * 2);
      cone.translate(tuftPos.x, tuftPos.y, tuftPos.z);
      parts.push(cone);
      crownTag.push(parts.length - 1);
    }
  }
  const g = mergeGeometries(parts, false)!;
  const tr = new Color(trunk);
  const lf = new Color(leaf);
  const lt = new Color(leafTip);
  return paint(g, (x, y, z, c) => {
    const r = Math.hypot(x, z);
    if (y > height * 0.78 && r > height * 0.08) c.copy(lf).lerp(lt, Math.min(1, (y - height * 0.8) / (height * 0.25)));
    else c.copy(tr).lerp(new Color('#7a6248'), Math.min(1, y / height));
  });
}

/** Cycad: stubby trunk + a crown of stiff fronds. */
export function cycadGeometry(size = 2.2, trunk = '#5a4630', leaf = '#2f6b32', tip = '#9fd24c'): BufferGeometry {
  const parts: BufferGeometry[] = [];
  const th = size * 0.55;
  const t = new CylinderGeometry(size * 0.16, size * 0.2, th, 8);
  t.translate(0, th / 2, 0);
  parts.push(t);
  const n = 10;
  for (let i = 0; i < n; i++) {
    const f = frond(size * 1.0, size * 0.16, 0.5, 5);
    f.rotateX(0.9 - (i % 2) * 0.25);
    f.rotateY((i / n) * Math.PI * 2);
    f.translate(0, th, 0);
    parts.push(f);
  }
  const g = mergeGeometries(parts, false)!;
  const tr = new Color(trunk);
  const lf = new Color(leaf);
  const tp = new Color(tip);
  return paint(g, (x, y, z, c) => {
    const r = Math.hypot(x, z);
    if (y > th * 0.95 || r > size * 0.22) c.copy(lf).lerp(tp, Math.min(1, r / size));
    else c.copy(tr);
  });
}

/** Displaced icosphere rock. */
export function rockGeometry(radius = 1, seed = 1, color = '#6b6258', color2 = '#3f3934'): BufferGeometry {
  const { IcosahedronGeometry } = require_three();
  const ico = new IcosahedronGeometry(radius, 2);
  ico.deleteAttribute('normal');
  ico.deleteAttribute('uv');
  const g = mergeVertices(ico);
  const p = g.getAttribute('position');
  let s = seed;
  const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  const ox = rnd() * 10, oy = rnd() * 10, oz = rnd() * 10;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const n = Math.sin(x * 2.1 + ox) * Math.cos(y * 1.7 + oy) * Math.sin(z * 2.3 + oz);
    const k = 1 + n * 0.28 + Math.sin(x * 5 + y * 4) * 0.04;
    p.setXYZ(i, x * k * 1.15, y * k * 0.75, z * k);
  }
  g.computeVertexNormals();
  const c1 = new Color(color);
  const c2 = new Color(color2);
  return paint(g, (_x, y, _z, c) => c.copy(c2).lerp(c1, Math.min(1, Math.max(0, y / radius * 0.6 + 0.6))));
}

/** Glowing mushroom cluster (emissive via vertex colour brightness). */
export function fungusGeometry(size = 0.35, stem = '#e8dcc0', cap = '#7cf5c8'): BufferGeometry {
  const parts: BufferGeometry[] = [];
  for (let i = 0; i < 3; i++) {
    const s = size * (0.6 + i * 0.25);
    const st = new CylinderGeometry(s * 0.12, s * 0.16, s, 6);
    st.translate(0, s / 2, 0);
    const cp = new ConeGeometry(s * 0.45, s * 0.35, 8);
    cp.translate(0, s + s * 0.1, 0);
    for (const gg of [st, cp]) {
      gg.translate(Math.sin(i * 2.1) * size * 0.5, 0, Math.cos(i * 2.1) * size * 0.5);
      parts.push(gg);
    }
  }
  const g = mergeGeometries(parts, false)!;
  const sc = new Color(stem);
  const cc = new Color(cap);
  return paint(g, (_x, y, _z, c) => c.copy(sc).lerp(cc, y > size * 0.7 ? 1 : 0));
}

/** Dead log: a bent tube. */
export function logGeometry(length = 4, radius = 0.35, color = '#4a3a2c'): BufferGeometry {
  const g = tubeGeometry({
    points: [new Vector3(-length / 2, 0, 0), new Vector3(0, radius * 0.4, radius * 0.3), new Vector3(length / 2, 0, 0)],
    radius: [radius * 0.8, radius, radius * 0.7],
    segments: 8,
    radial: 8,
  });
  const c1 = new Color(color);
  const c2 = new Color('#2d2119');
  return paint(g, (_x, y, _z, c) => c.copy(c2).lerp(c1, Math.min(1, Math.max(0, y / radius * 0.5 + 0.5))));
}

// Lazy import to keep the top of the file tidy.
import * as THREE from 'three';
function require_three(): typeof THREE {
  return THREE;
}
